import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Store, select } from '@ngrx/store'
import {
  Channel,
  Rental,
  ChannelNameTokens,
  untilDestroy,
  Destroyable,
  selectOnce,
  selectRentalById,
  isSomething,
  RatesChangedComplete,
  GroupRatesChangedComplete,
  selectSomeOnce,
  ActionFailed,
  ActionSkipped,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import {
  switchMap,
  take,
  tap,
  finalize,
  filter,
  debounceTime,
  map,
  startWith,
  concatMap,
  reduce,
  takeUntil,
  defaultIfEmpty,
  ignoreElements,
} from 'rxjs/operators'
import {
  selectAvailableRentalForNewAPIConnection,
  selectConnectionById,
} from '@tv3/store/connection/connection.selectors'
import { merge, Observable, of, timer } from 'rxjs'
import {
  AVAIL_PUSH_STAMP_FUTURE_SKEW,
  ChannelImportStatus,
  Connection,
  getRealChannelId,
  isRecentlyLinked,
  isTerminalImportStatus,
  stampedSinceLink,
} from '@tv3/store/connection/connection.model'
import {
  PushAvailabilityToConnectionForWizardComplete,
  HoldEventForEmptyCalendarPush,
  LinkConnectionWithRental,
  LoadConnectionsByChannel,
} from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { FormControl, Validators } from '@angular/forms'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'

@Component({
  selector: 'app-bdc-listing-wizard-step1',
  templateUrl: './bdc-listing-wizard-step1.component.html',
  styleUrls: ['./bdc-listing-wizard-step1.component.scss'],
})
export class BDCListingWizardStep1Component extends Destroyable implements OnInit {
  @Output() next = new EventEmitter()

  @Input() connection: Connection
  @Input() channel: Channel

  rentalIdCtrl = new FormControl('', [Validators.required])

  rentals$: Observable<Rental[]>

  private tmpHoldEventId: string

  isProcessing = false
  isLinked = false
  isBookingImported = false
  isAvailabilityPushed = false
  isRentalRatesReady = false
  ratesMissing: string[] = []

  // Latest recorded state of the bookings-import job, streamed by the connection helper
  // while the backend runs the import (TV4-5905).
  importStatus: ChannelImportStatus | null = null
  // A restored connection summary is refreshable by later store emissions. Live polling
  // remains authoritative unless the persisted state is genuinely newer or terminal.
  private importStatusRestoredFromConnection = false

  // Auto-chain (TV4-5906): after Link, the import runs as a tracked backend job and the
  // backend pushes availability itself once the import lands cleanly. This component
  // follows the job, then waits for the availability-push timestamp to appear on the
  // connection. Reopening the wizard resumes the chain instead of restarting it.
  autoStep: 'import' | 'availability' | null = null
  autoChainFailed = false
  private autoChainStarted = false

  get isStepComplete(): boolean {
    return this.isLinked && this.isBookingImported && this.isAvailabilityPushed
  }

  constructor(
    private store: Store<any>,
    private connectionHelperService: ConnectionHelperService,
    private actions: Actions,
    private toaster: Toaster
  ) {
    super()
  }

  ngOnInit(): void {
    this.rentals$ = this.store.pipe(
      selectOnce(selectAvailableRentalForNewAPIConnection, {
        id: this.channel.id,
        selected: this.connection.rentalId,
      })
    )

    this.rentalIdCtrl.setValue(this.connection.rentalId)
    if (this.connection.rentalId) {
      this.rentalIdCtrl.disable()
      this.isLinked = true
    }
    this.syncCompletedStepsFromConnection()

    // Keep the completion flags in step with the durable timestamps: the backend's
    // post-import availability push lands as lastavailpush through the connection
    // reloads, whether the chain below is waiting for it or the import ran manually.
    this.store
      .pipe(select(selectConnectionById, { id: this.connection.id }), filter(isSomething), untilDestroy(this))
      .subscribe((conn) => {
        // A re-link while the wizard is open changes what the flags mean: everything the
        // old ones said belonged to the previous rental. Reset them (they are one-way
        // sticky otherwise) — the stamps the re-link's own server-side chain writes will
        // seed them honestly again.
        if (conn.linkDate && this.connection?.linkDate && conn.linkDate !== this.connection.linkDate) {
          this.isBookingImported = false
          this.isAvailabilityPushed = false
          this.importStatus = null
          this.importStatusRestoredFromConnection = false
          this.autoChainStarted = false
          this.rentalIdCtrl.setValue(conn.rentalId)
        }
        this.connection = conn
        this.syncCompletedStepsFromConnection()
      })

    // save temporary hold event id
    this.actions
      .pipe(
        ofType(HoldEventForEmptyCalendarPush),
        tap(({ holdEvent }) => (this.tmpHoldEventId = holdEvent.id)),
        untilDestroy(this)
      )
      .subscribe()

    const rentalIdChanges$ = this.rentalIdCtrl.valueChanges.pipe(startWith(this.rentalIdCtrl.value))
    merge(this.actions.pipe(ofType(RatesChangedComplete, GroupRatesChangedComplete)), rentalIdChanges$)
      .pipe(
        debounceTime(500),
        map(() => this.rentalIdCtrl.value),
        filter(isSomething),
        switchMap((rentalId) => this.store.pipe(selectSomeOnce(selectRentalById(rentalId)))),
        filter(isSomething),
        // Silent readiness check: rates being incomplete no longer blocks this step,
        // the user reviews and completes rates on the Rates step instead.
        switchMap((rental) => this.connectionHelperService.getRentalRatesReadiness(rental, this.connection)),
        untilDestroy(this)
      )
      .subscribe(({ ready, missing }) => {
        this.isRentalRatesReady = ready
        this.ratesMissing = missing
      })

    // Resume: a linked connection with pending steps continues the automated chain
    // right away, so reopening the wizard acts as a progress view, not a restart.
    this.startAutoChain()
  }

  // Seeds the per-step completion flags from the durable timestamps on the connection —
  // the same fields the connection's Active state reads. This is what makes the wizard
  // resumable: the flags survive closing the dialog because the store has them. Stamps
  // older than the current rental link don't count: they are keyed to the channel room
  // and would otherwise mark a freshly re-linked rental's setup as already done.
  private syncCompletedStepsFromConnection() {
    this.isLinked = this.isLinked || !!this.connection?.rentalId
    this.isBookingImported = this.isBookingImported || stampedSinceLink(this.connection, this.connection?.lastavailpull)
    this.isAvailabilityPushed =
      this.isAvailabilityPushed ||
      stampedSinceLink(this.connection, this.connection?.lastavailpush, AVAIL_PUSH_STAMP_FUTURE_SKEW)

    const persistedImportStatus = this.connection?.importStatus
    if (!persistedImportStatus || !stampedSinceLink(this.connection, persistedImportStatus.updated)) {
      return
    }

    const currentImportStatus = this.importStatus
    const persistedUpdated = persistedImportStatus.updated || 0
    const currentUpdated = currentImportStatus?.updated || 0
    const sameJob = persistedImportStatus.job_id === currentImportStatus?.job_id
    const advancesSameJobToTerminal =
      sameJob &&
      !isTerminalImportStatus(currentImportStatus) &&
      isTerminalImportStatus(persistedImportStatus) &&
      persistedUpdated >= currentUpdated
    const refreshesRestoredStatus = this.importStatusRestoredFromConnection && persistedUpdated >= currentUpdated
    const isNewerPersistedStatus =
      persistedUpdated > currentUpdated && (!sameJob || !isTerminalImportStatus(currentImportStatus))

    if (!currentImportStatus || refreshesRestoredStatus || isNewerPersistedStatus || advancesSameJobToTerminal) {
      this.importStatus = persistedImportStatus
      this.importStatusRestoredFromConnection = true
    }
  }

  onRetryAutoChain() {
    this.autoChainStarted = false
    this.startAutoChain()
  }

  // Runs the remaining automated steps of this section in order: follow the bookings
  // import, then wait for the backend's post-import availability push to register.
  // Rates are NOT pushed from here — that decision lives on the Rates step. Only a
  // recently linked connection is automated: old links routinely lack post-link stamps
  // (cron sweeps never write them), and opening the wizard on one must stay a read-only
  // act instead of quietly starting a full-property import and its availability pushes.
  private startAutoChain() {
    if (this.autoChainStarted || !this.isLinked || this.isStepComplete || !isRecentlyLinked(this.connection)) {
      return
    }
    this.autoChainStarted = true
    this.autoChainFailed = false
    this.isProcessing = true

    this.runAutoImportBookings()
      .pipe(
        concatMap((ok) => (ok ? this.runAutoWaitAvailability() : of(false))),
        finalize(() => {
          this.isProcessing = false
          this.autoStep = null
        }),
        untilDestroy(this)
      )
      .subscribe(
        (ok) => {
          if (ok && this.isStepComplete) {
            this.next.emit()
          } else {
            this.autoChainFailed = true
          }
        },
        () => (this.autoChainFailed = true)
      )
  }

  // The import is a tracked backend job (TV4-5905); its recorded states stream through
  // importStatus for the progress alert. Only a recorded failure stops the chain: an
  // unknowable outcome (older backend, poll timeout) falls through to the availability
  // wait, which reads the durable timestamp and has its own deadline.
  private runAutoImportBookings(): Observable<boolean> {
    if (this.isBookingImported) {
      return of(true)
    }
    this.autoStep = 'import'
    this.importStatus = null
    this.importStatusRestoredFromConnection = false
    return this.connectionHelperService.importBookingsUnattended(ChannelNameTokens.BookingAPI, this.connection).pipe(
      tap((status) => {
        if (status) {
          this.importStatus = status
          this.importStatusRestoredFromConnection = false
        }
      }),
      reduce((_, status) => status, null as ChannelImportStatus | null),
      map((status) => {
        // Only a recorded 'done' marks the import complete: the flag enables the manual
        // Push Availability action, which must never run against a possibly-unfinished
        // import. An unknowable outcome still lets the chain proceed — the availability
        // wait below is the reality check (the backend pushes only after the import),
        // and the store watcher sets this flag honestly once the pull stamp arrives.
        this.isBookingImported = this.isBookingImported || status?.status === 'done'
        return status?.status !== 'failed'
      })
    )
  }

  // The backend pushes availability itself after a clean import (TV4-5906), so this step
  // waits for the push timestamp to land on the connection instead of pushing a second
  // time from here. A room the backend had to skip (for example, linked while an import
  // for the property was already running) never gets the stamp — the deadline expires
  // and the manual Push Availability button below stays available for it.
  private runAutoWaitAvailability(): Observable<boolean> {
    if (this.isAvailabilityPushed) {
      return of(true)
    }
    this.autoStep = 'availability'
    const channelId = getRealChannelId(this.connection.channelId)

    // Emission-less reload ticker: it only refreshes the store for the watcher below.
    // silent: a transient reload failure must retry quietly, not toast on every tick.
    const reloads$ = timer(0, 5000).pipe(
      takeUntil(timer(2 * 60 * 1000)),
      tap((tick) => {
        if (tick > 0) {
          this.store.dispatch(
            LoadConnectionsByChannel({ channelName: ChannelNameTokens.BookingAPI, channelId, silent: true })
          )
        }
      }),
      ignoreElements()
    )

    // The stamp is watched continuously rather than sampled once per tick, so a stamp
    // arriving from the previous tick's reload — or from the final one, covered by the
    // grace period past the last dispatch — is still observed.
    const stamp$ = this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      filter((conn) => stampedSinceLink(conn, conn?.lastavailpush, AVAIL_PUSH_STAMP_FUTURE_SKEW)),
      take(1),
      map(() => {
        this.isAvailabilityPushed = true
        // The push runs backend-side, so no action/effect exists to announce it — this
        // stamp arrival is the only completion signal the wizard receives (TV4-6050).
        this.toaster.success($localize`Availability pushed to Booking.com successfully.`)
        return true
      })
    )

    return merge(reloads$, stamp$).pipe(takeUntil(timer(2 * 60 * 1000 + 15 * 1000)), take(1), defaultIfEmpty(false))
  }

  onImport() {
    this.isProcessing = true
    this.isBookingImported = false
    this.importStatus = null
    this.importStatusRestoredFromConnection = false
    this.connectionHelperService
      .importBookings(ChannelNameTokens.BookingAPI, this.connection)
      .pipe(
        untilDestroy(this),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe({
        next: (status: ChannelImportStatus | null) => {
          if (status) {
            this.importStatus = status
            this.importStatusRestoredFromConnection = false
            this.isBookingImported = status.status === 'done'
          }
        },
      })
  }

  get importProgressText(): string {
    const status = this.importStatus
    const processed = status?.processed ?? status?.imported ?? 0
    const warningCount = this.importWarningCount
    const hasBreakdown =
      isSomething(status?.created_count) ||
      isSomething(status?.updated_count) ||
      isSomething(status?.already_existing_count)
    const breakdown = hasBreakdown
      ? $localize`${status?.created_count || 0} created, ${status?.updated_count || 0} updated, and ${
          status?.already_existing_count || 0
        } already existed.`
      : ''
    switch (status?.status) {
      case 'queued':
        return $localize`Booking import queued...`
      case 'in_progress':
        return status.total
          ? $localize`Processing reservations: ${processed} of ${status.total} processed so far...`
          : $localize`Processing reservations...`
      case 'done':
        if (status.total) {
          const result =
            processed < status.total
              ? $localize`Processed ${processed} of ${status.total} reservations for this Booking.com property.`
              : $localize`Processed ${processed} reservations for this Booking.com property.`
          const reportedFailures = status.errors?.filter((error) => error.error === 'import_failed').length || 0
          const reservationFailures = Math.max(reportedFailures, status.total - processed)
          const detailWarnings = Math.max(0, warningCount - reservationFailures)
          const warnings: string[] = []

          if (reservationFailures) {
            warnings.push(
              reservationFailures === 1
                ? $localize`1 reservation could not be processed.`
                : $localize`${reservationFailures} reservations could not be processed.`
            )
          }
          if (detailWarnings) {
            warnings.push(
              detailWarnings === 1
                ? $localize`1 reservation's details could not be fully refreshed.`
                : $localize`${detailWarnings} reservations' details could not be fully refreshed.`
            )
          }

          return `${result}${breakdown ? ` ${breakdown}` : ''}${warnings.length ? ` ${warnings.join(' ')}` : ''}`
        }
        return $localize`No reservations were found for this Booking.com property.`
      case 'failed':
        return status.error === 'enqueue_failed'
          ? $localize`The booking import could not be started. Please try again.`
          : $localize`The booking import failed. Please try the import again.`
      default:
        return ''
    }
  }

  get importWarningCount(): number {
    return this.importStatus?.status === 'done'
      ? Math.max(this.importStatus.errors?.length || 0, this.importStatus.warning_count || 0)
      : 0
  }

  get hasImportWarnings(): boolean {
    const status = this.importStatus
    const processed = status?.processed ?? status?.imported ?? 0
    return this.importWarningCount > 0 || (!!status?.total && processed < status.total)
  }

  onPushAvailability() {
    this.isProcessing = true
    this.connectionHelperService
      .pushAvailabilityForWizard(ChannelNameTokens.BookingAPI, this.connection, this.tmpHoldEventId)
      .pipe(
        // Any of these ends the attempt; only Complete marks the step done. Waiting on
        // Complete alone left isProcessing latched after a failed push, permanently
        // disabling this button (and Import, and Next) on the exact retry path the
        // empty-calendar dialog prescribes.
        switchMap(() =>
          this.actions.pipe(
            ofType(
              PushAvailabilityToConnectionForWizardComplete,
              HoldEventForEmptyCalendarPush,
              ActionSkipped,
              ActionFailed
            )
          )
        ),
        take(1),
        tap((action) => {
          if (action.type === PushAvailabilityToConnectionForWizardComplete.type) {
            this.isAvailabilityPushed = true
          }
        }),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe()
  }

  onRates() {
    this.store.dispatch(OpenRentalOverlay({ rentalId: this.rentalIdCtrl.value, activeTab: RentalDialogTab.Settings }))
  }

  onLink() {
    this.isProcessing = true
    this.store.dispatch(
      LinkConnectionWithRental({
        connId: this.connection.id,
        channelName: ChannelNameTokens.BookingAPI,
        data: {
          rentalId: this.rentalIdCtrl.value,
          channelId: this.connection.channelId,
          propertyId: this.connection.propertyId,
          roomId: this.connection.roomId,
        },
      })
    )

    // Success is detected on the store, not the action stream: ActionFailed is
    // application-wide, so an unrelated failure must not be mistaken for this link's.
    // rentalId lands on this connection only through LinkConnectionWithRentalComplete.
    const linked$ = this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      filter((connection) => !!connection?.rentalId),
      take(1)
    )

    // A failure anywhere just releases the spinner; the link watcher below stays armed
    // and still picks the link up if it completes later. Disarmed once linked so a later
    // unrelated failure cannot wipe the auto-chain's own spinner.
    this.actions
      .pipe(ofType(ActionFailed), take(1), takeUntil(linked$), untilDestroy(this))
      .subscribe(() => (this.isProcessing = false))

    linked$.pipe(untilDestroy(this)).subscribe(() => {
      // isProcessing must be released before starting the chain: the chain manages
      // the flag itself and a trailing reset here would wipe its spinner.
      this.isProcessing = false
      this.isLinked = true
      this.startAutoChain()
    })
  }
}
